import { createTheme } from '@mui/material/styles';
import type { Theme as AppTheme } from '../context/ThemeContext';

const statusColors = {
  success: '#28A745',
  warning: '#FFC107',
  error: '#DC3545',
  info: '#17A2B8',
};

export const createAppTheme = (appTheme: AppTheme) =>
  createTheme({
    palette: {
      mode: appTheme.isDark ? 'dark' : 'light',
      primary: {
        main: appTheme.primary,
        light: appTheme.primaryLight,
        dark: appTheme.primaryActive,
        contrastText: appTheme.contrastText,
      },
      secondary: {
        main: appTheme.secondary,
        contrastText: appTheme.contrastText,
      },
      success: {
        main: statusColors.success,
      },
      warning: {
        main: statusColors.warning,
      },
      error: {
        main: statusColors.error,
      },
      info: {
        main: statusColors.info,
      },
      background: {
        default: appTheme.background,
        paper: appTheme.surface,
      },
      text: {
        primary: appTheme.text,
        secondary: appTheme.textSecondary,
      },
      divider: appTheme.border,
    },
    typography: {
      fontFamily:
        '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      h1: {
        fontFamily: '"SF Pro Display", -apple-system, BlinkMacSystemFont, sans-serif',
        fontWeight: 800,
        fontSize: '2.5rem',
        lineHeight: 1.2,
      },
      h2: {
        fontFamily: '"SF Pro Display", -apple-system, BlinkMacSystemFont, sans-serif',
        fontWeight: 700,
        fontSize: '2rem',
        lineHeight: 1.3,
      },
      h3: {
        fontFamily: '"SF Pro Display", -apple-system, BlinkMacSystemFont, sans-serif',
        fontWeight: 600,
        fontSize: '1.5rem',
        lineHeight: 1.4,
      },
      button: {
        textTransform: 'none',
        fontWeight: 600,
      },
    },
    shape: {
      borderRadius: 12,
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            backgroundColor: 'var(--color-background-secondary)',
            color: 'var(--color-text-primary)',
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: '12px',
            padding: '12px 24px',
            fontSize: '16px',
            fontWeight: 600,
            textTransform: 'none',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.12)',
            '&:hover': {
              boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.14)',
              transform: 'translateY(-1px)',
            },
            transition: 'all 0.2s ease-in-out',
          },
          containedPrimary: {
            backgroundColor: 'var(--brand-primary)',
            color: 'var(--color-text-inverse)',
            '&:hover': {
              backgroundColor: 'var(--brand-hover)',
            },
          },
          outlinedPrimary: {
            borderColor: 'var(--color-primary)',
            color: 'var(--color-primary)',
            '&:hover': {
              borderColor: 'var(--color-primary-dark)',
              backgroundColor: 'var(--color-primary-50)',
            },
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            backgroundColor: 'var(--color-surface)',
            color: 'var(--color-text-primary)',
            borderRadius: '16px',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
            border: '1px solid var(--color-border)',
            '&:hover': {
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.12)',
              transform: 'translateY(-2px)',
            },
            transition: 'all 0.3s ease-in-out',
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              borderRadius: '12px',
              backgroundColor: 'var(--color-surface)',
              color: 'var(--color-text-primary)',
              '& fieldset': {
                borderColor: 'var(--color-border)',
              },
              '&:hover fieldset': {
                borderColor: 'var(--color-primary)',
              },
              '&.Mui-focused fieldset': {
                borderColor: 'var(--color-primary)',
                borderWidth: '2px',
              },
            },
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: '20px',
            fontWeight: 500,
          },
        },
      },
    },
  });
